import { useEffect } from 'react'
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import { OnboardingGate } from './components/OnboardingGate'
import { setupAnalyticsListeners, trackScreenChange } from './utils/analytics'
import { IS_FUTURA } from './constants/futura'
import { BuySell } from './pages/Buy'
import { DashboardHistory } from './pages/Dashboard/DashboardHistory'
import { DashboardOtcRequests } from './pages/Dashboard/OtcRequests'
import { Buy } from './pages/Buy/Transact/Buy'
import { DashboardProfile } from './pages/Dashboard/DashboardProfile'
import { DashboardTrade } from './pages/Dashboard/DashboardTrade'
import Confirmation from './pages/Orders/Confirmation'
import OrderStatus from './pages/Orders/OrderStatus'
import { Signup } from './pages/Signup'
import Upload from './pages/Upload/Upload'
import { EmailLoginPanel } from './panels/login/EmailLoginPanel'
import KeyLoginPanel from './panels/login/KeyLoginPanel'
import { LoginVerifyPanel } from './panels/login/LoginVerifyPanel'
import { CompleteRegistrationStandard } from './panels/signup/CompleteRegistrationStandard'
import { RegistrationFailure } from './panels/signup/RegistrationFailure'
import KycRedirectFailed from './panels/signup/KycRedirectFailed'
import { KYCRefreshInfo } from './panels/signup/KYCRefresh'
import KycRedirectSuccess from './panels/signup/KycRedirectSuccess'
// import PaytrieWidget from "./PaytrieWidget";

import { CorporateSignup } from './pages/CorporateSignup/CorporateSignup'
import { CorporateGate } from './components/CorporateGate'
import {
  CORPORATE_LOGIN_PATH,
  CORPORATE_SIGNUP_PATHS,
  CORPORATE_UPLOAD_PATH,
  CORPORATE_VERIFY_PATH,
} from './constants/corporateRoutePaths'
import { CorporateAdditionalDocumentsPage } from './panels/corporatesignup/CorporateAdditionalDocumentsPage'
import { CorporateLoginPanel } from './panels/corporatesignup/CorporateLoginPanel'
import OtcSubmit from './pages/Otc/OtcSubmit'
import OtcRequestStatus from './pages/Otc/OtcRequestStatus'
import { MaintenanceStatusPage } from './pages/Maintenance/MaintenanceStatusPage'

const RegisterRedirect = () => {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/SignupEmail', search, hash }} replace />
}

const LoginRedirect = () => {
  const location = useLocation()
  return <Navigate to="/LandingLogin" replace state={location.state} />
}

// Corporate routes configuration
const corporateSignupRoutes = CORPORATE_SIGNUP_PATHS.map((path) => ({
  path,
  element: <CorporateSignup />,
}))

export const corporateRoutes = [
  {
    path: CORPORATE_LOGIN_PATH,
    element: <CorporateLoginPanel />,
  },
  {
    path: CORPORATE_VERIFY_PATH,
    element: <LoginVerifyPanel />,
  },
  {
    element: <CorporateGate />,
    children: [
      ...corporateSignupRoutes,
      {
        path: CORPORATE_UPLOAD_PATH,
        element: <CorporateAdditionalDocumentsPage />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/corporate" replace />,
  },
]

const baseRoutesFutura = [
  {
    path: '/',
    element: <Signup />,
  },
  {
    path: '/Signup',
    element: <Signup />,
  },
  {
    path: '/SignupEmail',
    element: <Signup />,
  },
  {
    path: '/register',
    element: <RegisterRedirect />,
  },
  {
    path: '/SignupPhone',
    element: <Signup />,
  },
  {
    path: '/SignupVerifySMS',
    element: <Signup />,
  },
  {
    path: '/SignupDisclosure',
    element: <Signup />,
  },
  {
    path: '/SignupUserInfo',
    element: <Signup />,
  },
  {
    path: '/SignupOccupation',
    element: <Signup />,
  },
  {
    path: '/SignupAdditionalInfo',
    element: <Signup />,
  },
  {
    path: '/SignupAdditionalInformation2',
    element: <Signup />,
  },
  {
    path: '/SignupFraudWarningFollowUp',
    element: <Signup />,
  },
  {
    path: '/SignupKYC',
    element: <Signup />,
  },
  {
    path: '/Complete',
    element: <CompleteRegistrationStandard />,
  },
  {
    path: '/Failed',
    element: <RegistrationFailure />,
  },
  {
    path: '/RedirectSuccess',
    element: <KycRedirectSuccess />,
  },
  {
    path: '/RedirectFailed',
    element: <KycRedirectFailed />,
  },
  {
    path: '*',
    element: <Signup />,
  },
]

const baseRoutesFull = [
  {
    path: '/',
    element: <BuySell />,
  },
  {
    path: '/Signup',
    element: <Signup />,
  },
  {
    path: '/SignupEmail',
    element: <Signup />,
  },
  {
    path: '/register',
    element: <RegisterRedirect />,
  },
  {
    path: '/SignupPhone',
    element: <Signup />,
  },
  {
    path: '/SignupVerifySMS',
    element: <Signup />,
  },
  {
    path: '/SignupDisclosure',
    element: <Signup />,
  },
  {
    path: '/SignupUserInfo',
    element: <Signup />,
  },
  {
    path: '/SignupOccupation',
    element: <Signup />,
  },
  {
    path: '/SignupAdditionalInfo',
    element: <Signup />,
  },
  {
    path: '/SignupAdditionalInformation2',
    element: <Signup />,
  },
  {
    path: '/SignupFraudWarningFollowUp',
    element: <Signup />,
  },
  {
    path: '/SignupKYC',
    element: <Signup />,
  },
  {
    path: '/EasyLogin',
    element: <EmailLoginPanel landing={false} />,
  },
  {
    path: '/LandingLogin',
    element: <EmailLoginPanel landing={true} />,
  },
  {
    path: '/Login',
    element: <LoginRedirect />,
  },
  {
    path: '/KeyLogin',
    element: <KeyLoginPanel />,
  },
  {
    path: '/LoginVerify',
    element: <LoginVerifyPanel />,
  },
  {
    path: '/Complete',
    element: <CompleteRegistrationStandard />,
  },
  {
    path: '/Failed',
    element: <RegistrationFailure />,
  },
  {
    path: '/RedirectSuccess',
    element: <KycRedirectSuccess />,
  },
  {
    path: '/RedirectFailed',
    element: <KycRedirectFailed />,
  },
  {
    path: '/Status',
    element: <OrderStatus />,
  },
  {
    path: '/Confirmation',
    element: <Confirmation />,
  },
  {
    path: '/Upload',
    element: <Upload />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/Transactions',
        element: <Buy />,
      },
      {
        path: '/Dashboard',
        element: <DashboardTrade />,
      },
      {
        path: '/History',
        element: <DashboardHistory />,
      },
      {
        path: '/otc-requests',
        element: <DashboardOtcRequests />,
      },
      {
        path: '/otc-requests/:id',
        element: <OtcRequestStatus />,
      },
      {
        path: '/Profile',
        element: <DashboardProfile />,
      },
      {
        path: '/SignupKycRefresh',
        element: <KYCRefreshInfo />,
      },
      { path: '/otc-submit', element: <OtcSubmit /> },
    ],
  },
  {
    path: '*',
    element: <BuySell />,
  },
]

const baseRoutes = IS_FUTURA ? baseRoutesFutura : baseRoutesFull

const IS_CORPORATE = import.meta.env.VITE_ENABLE_CORPORATE === 'true'

const router = createBrowserRouter(
  IS_CORPORATE
    ? corporateRoutes
    : [
        {
          path: '/system-status',
          element: <MaintenanceStatusPage />,
        },
        {
          element: <OnboardingGate />,
          children: baseRoutes,
        },
      ]
)

router.subscribe((state) => {
  if (state.navigation.state === 'idle') {
    trackScreenChange(state.location.pathname)
  }
})

const App = () => {
  useEffect(() => {
    setupAnalyticsListeners()
    trackScreenChange(window.location.pathname)
  }, [])

  return (
    <div className="w-full md:flex md:justify-center md:items-center">
      <div className="lg:max-w-[730px] lg:w-[730px]">
        <RouterProvider router={router} />
      </div>
    </div>
  )
}

export default App
